/**
 * WritingResumeHero — show-not-tell's OWN resume affordance.
 *
 * Implements BUG-20260718-154634 (C-2). MIRRORS comprehension's
 * ResumeSessionHero (frontend/src/components/ResumeSessionHero.tsx — a
 * READ-ONLY pattern reference; never imported or edited, per BR-TS004.2 /
 * BR-009 slice isolation) so the writing-sessions landing page
 * (WritingHistoryPage) behaves identically to comprehension's
 * SessionsListPage: when the student has an active writing session, this
 * hero replaces the "Start a new session" affordance with a single
 * "Continue" action.
 *
 * Enforces TO-LL-021 (mirrored principle): ONE resume action only — no
 * second standalone resume button anywhere near this component.
 * Enforces TO-LL-013: the navigate target is built from a validated
 * internal session id (ROUTES.WRITING_PRACTICE + ?sessionId=) — never a
 * server-supplied free-form URL. Reuses the SAME `?sessionId=` navigation
 * contract WritingSessionRow already uses (WritingPracticePage.openSession),
 * so "Continue" opens/resumes the session via show-not-tell's OWN existing
 * flow — no new resume mechanism is introduced.
 *
 * Purely presentational: no data fetching, no React Query, no Zustand. If
 * activeSession is undefined/null the component renders nothing (defensive
 * guard, mirrors ResumeSessionHero).
 */

import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ROUTES } from '@/constants/routes';
import type { ActiveWritingSessionSummary } from '@/types/writingPractice';

export interface WritingResumeHeroProps {
  /** The student's current active writing session. Renders null if absent. */
  activeSession: ActiveWritingSessionSummary | null | undefined;
}

export function WritingResumeHero({ activeSession }: WritingResumeHeroProps) {
  const navigate = useNavigate();

  // Defensive guard (mirrors ResumeSessionHero's TO-LL-021 guard).
  if (!activeSession) {
    return null;
  }

  return (
    <div className="relative mb-2 flex flex-col overflow-hidden rounded-3xl border border-orange-200 bg-gradient-to-br from-orange-50 via-amber-50 to-orange-100 p-6 shadow-lg">
      <p className="relative z-[1] mb-3 text-[11px] font-extrabold uppercase tracking-wider text-otter-orange-600">
        <span aria-hidden="true">&#9654;</span> In progress
      </p>

      <h2 className="relative z-[1] mb-1 font-display text-xl font-semibold text-otter-earth-800">
        You have an open writing session
      </h2>

      {/* Locked-slot message — mirrors ResumeSessionHero's exact copy pattern
          (BR-TS006.1 one-active-per-child); aria-live so screen readers
          announce it when it appears. */}
      <p className="relative z-[1] mb-4 text-sm text-otter-earth-700" role="status" aria-live="polite">
        Finish this session before you can start a new one
      </p>

      <div className="relative z-[1]">
        <Button
          type="button"
          variant="warm"
          size="lg"
          className="h-12 w-full sm:w-auto"
          aria-label="Resume your writing session"
          onClick={() =>
            navigate(
              `${ROUTES.WRITING_PRACTICE}?sessionId=${encodeURIComponent(activeSession.sessionId)}`
            )
          }
        >
          Resume session
        </Button>
      </div>
    </div>
  );
}
